"use strict";

Object.defineProperty(exports, "__esModule", {
  value: true
});
exports.alertDialog = alertDialog;
exports.openInputDialog = openInputDialog;
require("../styles/dialog.css");
var _litHtml = require("lit-html");
var _profileIcons = require("../icons-svg/profileIcons");
/* Copied from issue-pane, minor typescript adjustments */
/* Changed modal from div to dialog element */
let modalDialog = null;
let previousFocus = null;
function ensureModalDialog(dom) {
  // if we previously created a dialog but it was removed from the document
  // (tests clear body), rebuild it. Checking presence ensures our reference
  // doesn't point at a detached element.
  if (modalDialog && dom.body.contains(modalDialog)) return modalDialog;
  modalDialog = null;
  modalDialog = dom.createElement('dialog');
  modalDialog.id = 'profile-modal';
  modalDialog.setAttribute('aria-modal', 'true');
  modalDialog.setAttribute('aria-labelledby', 'modal-title');
  modalDialog.setAttribute('aria-describedby', 'modal-desc');
  modalDialog.innerHTML = `
    <div class="modal">
      <div class="modal-header">
        <h2 id="modal-title"></h2>
        <div id="modal-header-action"></div>
      </div>
      <div id="modal-desc"></div>
      <section id="modal-error" class="modal__error-section" aria-live="assertive" role="alert" hidden></section>
      <div id="modal-buttons"></div>
    </div>
  `;
  dom.body.appendChild(modalDialog);
  return modalDialog;
}
function getDialogElements(dialog) {
  return {
    dialog,
    title: dialog.querySelector('#modal-title'),
    headerAction: dialog.querySelector('#modal-header-action'),
    description: dialog.querySelector('#modal-desc'),
    error: dialog.querySelector('#modal-error'),
    buttons: dialog.querySelector('#modal-buttons')
  };
}
function clearModalError(elements) {
  elements.error.textContent = '';
  elements.error.hidden = true;
}
function setModalError(elements, message) {
  elements.error.textContent = message;
  elements.error.hidden = false;
}
function openDialogElement(dialog) {
  if (typeof dialog.showModal === 'function') {
    try {
      if (!dialog.open) dialog.showModal();
      return;
    } catch {
      // Fall back for jsdom or partial dialog implementations.
    }
  }
  dialog.setAttribute('open', '');
}
function closeDialogElement(dialog) {
  if (typeof dialog.close === 'function' && dialog.open) {
    dialog.close();
    return;
  }
  dialog.removeAttribute('open');
}
function findInitialContentFocusTarget(container) {
  const focusable = Array.from(container.querySelectorAll('input, select, textarea, [contenteditable="true"], [tabindex]:not([tabindex="-1"])'));
  for (const el of focusable) {
    const isDisabled = el.hasAttribute('disabled') || el.getAttribute('aria-disabled') === 'true';
    const hiddenByAttr = el.hasAttribute('hidden') || el.getAttribute('aria-hidden') === 'true';
    if (!isDisabled && !hiddenByAttr) return el;
  }
  return null;
}
function focusInitialDialogTarget(description, buttons) {
  const initialInput = findInitialContentFocusTarget(description);
  if (initialInput) {
    initialInput.focus();
    if (initialInput instanceof HTMLInputElement || initialInput instanceof HTMLTextAreaElement) {
      initialInput.select();
    }
    return;
  }
  const firstButton = buttons.querySelector('button');
  if (firstButton) firstButton.focus();
}
function collectFormValues(form) {
  const data = new FormData(form);
  const values = {};
  data.forEach((value, key) => {
    values[key] = String(value);
  });
  return values;
}
function openModal({
  title,
  message,
  buttons,
  headerAction,
  dom
}) {
  const dialog = ensureModalDialog(dom);
  const elements = getDialogElements(dialog);
  previousFocus = dom.activeElement;
  openDialogElement(dialog);
  elements.title.textContent = title || '';
  elements.description.innerHTML = '';
  if (typeof message === 'string') {
    elements.description.textContent = message;
  } else {
    elements.description.appendChild(message);
  }
  elements.buttons.innerHTML = '';
  clearModalError(elements);
  return new Promise(resolve => {
    const finish = value => {
      closeModal(value);
      resolve(value);
    };
    const requestCancel = () => {
      const cancelBtn = dialog.querySelector('button[data-cancel]');
      if (cancelBtn) {
        cancelBtn.click();
        return;
      }
      finish(false);
    };
    dialog.oncancel = event => {
      event.preventDefault();
      requestCancel();
    };
    elements.headerAction.innerHTML = '';
    const resolvedHeaderAction = headerAction || {
      type: 'close'
    };
    if (resolvedHeaderAction.type === 'close') {
      const closeButton = dom.createElement('button');
      closeButton.setAttribute('type', 'button');
      closeButton.setAttribute('aria-label', 'Close dialog');
      (0, _litHtml.render)(_profileIcons.closeIcon, closeButton);
      closeButton.onclick = () => requestCancel();
      elements.headerAction.appendChild(closeButton);
    } else if (resolvedHeaderAction.type === 'button') {
      const actionButton = dom.createElement('button');
      actionButton.setAttribute('type', 'button');
      actionButton.className = resolvedHeaderAction.className || 'modal__header-action-button profile__action-button profile-action-text flex-center';
      actionButton.textContent = resolvedHeaderAction.label;
      actionButton.setAttribute('aria-label', resolvedHeaderAction.ariaLabel || resolvedHeaderAction.label);
      actionButton.onclick = async () => {
        if (resolvedHeaderAction.onClick) {
          await resolvedHeaderAction.onClick();
        }
      };
      elements.headerAction.appendChild(actionButton);
    }
    buttons.forEach(btn => {
      const b = dom.createElement('button');
      b.setAttribute('type', 'button');
      b.textContent = btn.label;
      if (btn.primary) b.classList.add('btn-primary');
      if (btn.cancel) {
        b.classList.add('btn-light');
        b.setAttribute('data-cancel', 'true');
      }
      b.addEventListener('click', async () => {
        if (btn.beforeClose) {
          const shouldClose = await btn.beforeClose();
          if (!shouldClose) return;
        }
        finish(btn.value);
      });
      elements.buttons.appendChild(b);
    });
    focusInitialDialogTarget(elements.description, elements.buttons);
  });
}
function closeModal(_result) {
  if (modalDialog) {
    closeDialogElement(modalDialog);
    modalDialog.oncancel = null;
    const headerActionButton = modalDialog.querySelector('#modal-header-action button');
    if (headerActionButton) headerActionButton.onclick = null;
    if (previousFocus && 'focus' in previousFocus) previousFocus.focus();
  }
}
function alertDialog(message, title = 'Information', dom) {
  return openModal({
    title,
    message,
    buttons: [{
      label: 'OK',
      value: true,
      primary: true
    }],
    dom
  });
}
/* Function below generated by AI Model: GPT-5.3-Codex */
/* Prompt: write me a reusable function to open a dialog with a form and return the form values on submit. 
The function should also support validation and display error messages in the dialog. 
It should reuse the OpenModal function for consistency. Done in 2 passes */
function openInputDialog(options) {
  const submitLabel = options.submitLabel || 'Save Changes';
  const cancelLabel = options.cancelLabel || 'Cancel';
  const dialog = ensureModalDialog(options.dom);
  const elements = getDialogElements(dialog);
  const submitProxy = options.dom.createElement('button');
  submitProxy.type = 'submit';
  submitProxy.hidden = true;
  submitProxy.tabIndex = -1;
  options.form.appendChild(submitProxy);
  const handleSubmit = event => {
    event.preventDefault();
    const saveButton = dialog.querySelector('#modal-buttons .btn-primary');
    saveButton?.click();
  };
  options.form.addEventListener('submit', handleSubmit);
  return openModal({
    title: options.title,
    message: options.form,
    headerAction: options.headerAction,
    buttons: [{
      label: cancelLabel,
      value: null,
      cancel: true
    }, {
      label: submitLabel,
      value: 'save',
      primary: true,
      beforeClose: async () => {
        clearModalError(elements);
        if (options.validate) {
          const validationMessage = await options.validate();
          if (validationMessage) {
            setModalError(elements, validationMessage);
            return false;
          }
        }
        if (!options.onSave) return true;
        try {
          await options.onSave();
          return true;
        } catch (error) {
          const fallback = error instanceof Error ? error.message : String(error);
          const message = options.formatSaveError ? options.formatSaveError(error) : fallback;
          setModalError(elements, message);
          return false;
        }
      }
    }],
    dom: options.dom
  }).then(result => {
    if (result !== 'save') return null;
    return collectFormValues(options.form);
  }).finally(() => {
    options.form.removeEventListener('submit', handleSubmit);
    submitProxy.remove();
  });
}